import React from 'react';
import { AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import { cn } from '../../../utils';
import type { AdminActionBanner } from './usdWalletLedgerUtils';

export function AdminActionBannerPanel({ banner }: { banner: AdminActionBanner }) {
  const styles =
    banner.tone === 'info'
      ? 'border-sky-200 bg-sky-50 text-sky-950'
      : banner.tone === 'warning'
        ? 'border-amber-200 bg-amber-50 text-amber-950'
        : banner.tone === 'success'
          ? 'border-emerald-200 bg-emerald-50 text-emerald-950'
          : 'border-rose-200 bg-rose-50 text-rose-950';

  const Icon =
    banner.tone === 'error'
      ? AlertCircle
      : banner.tone === 'success'
        ? CheckCircle2
        : Clock;

  return (
    <div className={cn('rounded-xl border px-4 py-3', styles)}>
      <div className="flex gap-3">
        <Icon className="mt-0.5 h-5 w-5 shrink-0 opacity-80" aria-hidden />
        <div>
          <p className="text-sm font-semibold">{banner.title}</p>
          <p className="mt-1 text-sm leading-relaxed opacity-90">{banner.body}</p>
        </div>
      </div>
    </div>
  );
}

export function CopyableAddress({
  address,
  compact = false,
}: {
  address: string;
  /** Inline, left-aligned — for flow cards */
  compact?: boolean;
}) {
  const [copied, setCopied] = React.useState(false);

  const onCopy = async () => {
    if (!address) return;
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  if (!address) return compact ? null : <span>—</span>;

  if (compact) {
    return (
      <div className="mt-1.5 flex items-start gap-1.5">
        <span className="min-w-0 flex-1 break-all font-mono text-[11px] leading-snug text-slate-700">
          {address}
        </span>
        <button
          type="button"
          onClick={() => void onCopy()}
          className="shrink-0 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-slate-600 hover:bg-slate-50"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-start justify-end gap-2">
      <span className="break-all font-mono text-xs text-slate-800">{address}</span>
      <button
        type="button"
        onClick={() => void onCopy()}
        className="shrink-0 rounded border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600 hover:bg-slate-50"
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}
